/* -------------------------- Employee Card ------------------------------- */
import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Trash2, ChevronDown, ChevronRight } from "lucide-react";
import {
  RadioGroup,
  RadioGroupItem,
} from "../../components/ui/radio-group";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../../components/ui/accordion";
import { Checkbox } from "../../components/ui/checkbox";
import { motion, AnimatePresence } from "framer-motion";
import { IdentitySection } from "./IdentitySection";
import {PERMISSIONS} from "../steps/Step1Employees"

export const EmployeeCard = ({ userSearch, bizSearch, businessId,
  employee, index, patch, remove, errorMsgs = [] }) => {
    const toggleExpanded = () => patch({ expanded: !employee.expanded });


  const setField = (key, val) => patch({ [key]: val });
  const [openAccordion, setOpenAccordion] = useState(""); // or use a different state name if needed

  /* ------ permissions toggle (only for MANAGING_ACCOUNT) ------- */
  const togglePerm = (permKey) =>
    patch({
      permissions: {
        ...employee.permissions,
        [permKey]: !employee.permissions[permKey],
      },
    });

  /* collapsed header label */
  const headerLabel =
    employee.fullName?.trim() || `Employee ${index + 1}`;
  const roleColor =
    employee.roleType === "SERVICE_PROVIDER" ? "bg-primary/10 text-primary" : "bg-secondary text-foreground";

  return (
    <Card className="border shadow-sm overflow-visible">
      {/* Header */}
      <CardHeader className="flex flex-row items-center justify-between">
        <div
          className="flex items-center gap-2 cursor-pointer"
          onClick={toggleExpanded}
        >
          {employee.expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          <CardTitle className="flex items-center gap-2 text-base">
            {headerLabel}
            <span className={`px-2 py-0.5 rounded text-xs ${roleColor}`}>
              {employee.roleType === "SERVICE_PROVIDER" ? "Service Provider" : "Manager"}
            </span>
          </CardTitle>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            remove();
          }}
        >
          <Trash2 size={16} />
        </Button>
      </CardHeader>


      {/* Animated Content */}
      <AnimatePresence initial={false}>
        {employee.expanded && (
         <motion.div
           className="overflow-visible"
           initial={{ height: 0, opacity: 0 }}
           animate={{ height: 'auto', opacity: 1 }}
           exit={{ height: 0, opacity: 0 }}
           transition={{ duration: 0.25 }}
         >
            <CardContent className="space-y-6 pt-0 pb-4">
              {errorMsgs.length > 0 && (
                <ul className="space-y-1 text-sm text-red-600">
                  {errorMsgs.map((m, i) => (
                    <li key={i}>• {m}</li>
                  ))}
                </ul>
              )}
              {/* Role Selection */}
              <div className="space-y-2">
                <p className="font-medium">Role type</p>
                <RadioGroup
                  value={employee.roleType}
                  onValueChange={(val) => {
                    if (val !== employee.roleType) {
                      patch({ roleType: val });   // update only when it actually changed
                    }
                  }}
                  className="flex gap-4"
                >
                  <RadioGroupItem value="SERVICE_PROVIDER" id={`${employee.id}-sp`}>
                    Service Provider
                  </RadioGroupItem>
                  <RadioGroupItem value="MANAGING_ACCOUNT" id={`${employee.id}-ma`}>
                    Managing Account
                  </RadioGroupItem>
                </RadioGroup>
              </div>

              {/* Identity Selection */}
              <IdentitySection
                  employee={employee}
                  setField={setField}
                  userSearch={userSearch}
                  bizSearch={bizSearch}
                  businessId={businessId}
              />
              {/* Permissions (only for managers) */}
              {employee.roleType === "MANAGING_ACCOUNT" && (
              <Accordion
                type="single"
                collapsible
                value={openAccordion}
                onValueChange={setOpenAccordion}
                className="border rounded"
              >
                <AccordionItem value="perms">
                  <AccordionTrigger>Permissions</AccordionTrigger>
                    <AccordionContent className="divide-y">
                      {/* Optional header row */}
                      <div className="grid grid-cols-3 text-xs text-gray-500 mb-1">
                        <span></span>
                        <span className="text-center">Enabled</span>
                        <span className="text-center">Require Approval</span>
                      </div>

                      {PERMISSIONS.map((perm) => {
                        const enabled = employee.permissions[perm.key];
                        return (
                          <div
                            key={perm.key}
                            className="grid grid-cols-3 items-center py-2 gap-2"
                          >
                            {/* Permission label */}
                            <span className="col-span-1">{perm.label}</span>

                            {/* Enable checkbox */}
                            <div className="flex justify-center">
                              <Checkbox
                                checked={enabled}
                                onCheckedChange={() => togglePerm(perm.key)}
                              />
                            </div>

                            {/* Require manual approval checkbox (disabled if not enabled) */}
                            <div className="flex justify-center">
                              <Checkbox
                                checked={employee.requireApproval?.[perm.key] || false}
                                disabled={!enabled}
                                onCheckedChange={() =>
                                  patch({
                                    requireApproval: {
                                      ...employee.requireApproval,
                                      [perm.key]: !employee.requireApproval?.[perm.key],
                                    },
                                  })
                                }
                              />
                            </div>
                          </div>
                        );
                      })}
                    </AccordionContent>
                </AccordionItem>
              </Accordion>
              )}
            </CardContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
};
